/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

const domain = process.env.REACT_APP_AD_B2C_DOMAIN || '';
const subdomain = process.env.REACT_APP_AD_B2C_SUBDOMAIN || '';
const b2cSignIn = process.env.REACT_APP_AD_B2C_SIGN_IN || '';
const b2cForgotPassword = process.env.REACT_APP_AD_B2C_FORGOT_PASSWORD || '';
const b2cEditProfile = process.env.REACT_APP_AD_B2C_EDIT_PROFILE || '';
const clientId = process.env.REACT_APP_AD_B2C_CLIENT_ID || '';

export const AUTHORITY = {
    SING_IN: `https://${domain}/${subdomain}/${b2cSignIn}`,
    FORGOT_PASSWORD: `https://${domain}/${subdomain}/${b2cForgotPassword}`,
    EDIT_PROFILE: `https://${domain}/${subdomain}/${b2cEditProfile}`,
};

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */

export const b2cPolicies = {
    names: {
        signUpSignIn: b2cSignIn,
        forgotPassword: b2cForgotPassword,
        editProfile: b2cEditProfile,
    },
    authorities: {
        signUpSignIn: {
            authority: AUTHORITY.SING_IN,
        },
        forgotPassword: {
            authority: AUTHORITY.FORGOT_PASSWORD,
        },
        editProfile: {
            authority: AUTHORITY.EDIT_PROFILE,
        },
    },
    authorityDomain: domain,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
        clientId: clientId, // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    apiTodoList: {
        endpoint: 'http://localhost:5000/api/todolist',
        scopes: {
            read: [`https://${subdomain}/api-gateway/gateway.read`],
            write: [`https://${subdomain}/api-gateway/gateway.write`],
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.apiTodoList.scopes.read, ...protectedResources.apiTodoList.scopes.write],
};
